/**
 * Sortable assignment table with status tracking and result navigation.
 *
 * @module components/dashboard/AssignmentTable
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import ArrowRightAltIcon from "@material-ui/icons/ArrowRightAlt";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Pagination from '@material-ui/lab/Pagination';
import React, { useEffect } from "react";
import {
    makeStyles,
    withStyles,
    createTheme,
} from "@material-ui/core/styles";
import VisibilityIcon from "@material-ui/icons/Visibility";
import moment from "moment";
import Avatar from "@material-ui/core/Avatar";
import { getAssignmentHistory } from '../../services/result.service';
import {
    ThemeProvider,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogContentText,
    DialogActions,
    CircularProgress,
    Paper,
} from "@material-ui/core";
import { indigo } from "@material-ui/core/colors";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from '@material-ui/icons/Edit';
import StyledLink from "../common/StyledLink";
import {
    handleShowReviewerButtons,
    saveAssignmentNotes,
    deleteAssignment,
    saveCurrentSimProductType
} from "../../redux/sims/actions";
import DashboardPermissions from "../../permissions/dashboard";
import { permissionCheck } from "../../utils/permissionCheck";
import EventNoteIcon from "@material-ui/icons/EventNote";
import noAssignments from "../../assets/images/noAssignments.png";
import SimsPermissions from "../../permissions/sims";
import AssignmentTableStyle from './AssignmentTable';
import ScreenShareIcon from "@material-ui/icons/ScreenShare";
import VideocamIcon from "@material-ui/icons/Videocam";

const innerTheme = createTheme({
    palette: {
        primary: indigo,
    },
});

const StyledTableRow = withStyles(() => ({
}))(TableRow);

/**
 * Represents a functional component responsible for rendering and managing an assignment table.
 *
 * The `AssignmentTable` component provides a comprehensive interface for displaying, interacting with,
 * and managing assignments. It includes functionality for:
 * - Sorting and ordering the table data.
 * - Pagination to navigate through large datasets.
 * - Handling actions such as adding, editing, and deleting assignments.
 * - Managing assignment-specific dialogs (notes, delete confirmation, track record).
 * - Displaying pass/fail history and assignment metadata.
 * - Providing contextual actions based on the user's permissions and active tab.
 *
 * @param {Object} props - The props object.
 * @param {Array<Object>} props.assignments - The list of assignments displayed in the table.
 *     Each assignment is represented by an object containing relevant data.
 * @param {number | string} props.value - The active tab or context identifier.
 * @param {number} props.total - The total count of assignments.
 * @param {Array<string>} props.sortProperties - The list of properties available for sorting the table.
 * @param {string} props.order - The current sorting order (e.g., "asc" or "desc").
 * @param {string} props.orderBy - The property by which the data is currently sorted.
 * @param {Function} props.handleShowReviewerButtons - A callback to handle actions related to showing
 *     reviewer-related buttons in the UI.
 * @param {boolean} props.inReadyReviewTab - Indicates whether the component is in the "Ready for Review" tab.
 * @param {Function} props.saveAssignmentNotes - A function responsible for saving notes related to an assignment.
 * @param {Function} props.deleteAssignment - A function responsible for deleting the specified assignment.
 * @param {Function} props.refreshCount - A function to refresh the assignment count after a modification.
 * @param {Function} props.addAssignment - A function to add a new assignment to the list.
 * @param {boolean} props.assignment_loading - Indicates whether the assignments are currently being loaded.
 * @param {Function} props.handlePageChange - A callback to handle pagination changes.
 * @param {number} props.page - The current page number of the displayed assignments.
 * @param {Function} props.saveCurrentSimProductType - A function to persist the current simulation product type.
 *
 * @returns {React.Element} A React functional component that renders the assignment table and manages its behavior.
 */
const AssignmentTable = ({
    assignments,
    value,
    total,
    sortProperties,
    order,
    orderBy,
    handleShowReviewerButtons,
    //inMyAssignmentTab,
    inReadyReviewTab,
    saveAssignmentNotes,
    deleteAssignment,
    refreshCount,
    addAssignment,
    assignment_loading,
    handlePageChange,
    page,
    saveCurrentSimProductType
}) => {
    const loggedUser = JSON.parse(localStorage.getItem('user'));
    const userId = loggedUser?._id;
    const tabName = value.toLowerCase(),
        canViewNames = permissionCheck(
            DashboardPermissions.user_name.view.task,
            DashboardPermissions.user_name.view.type
        ),
        showAssignedTo = canViewNames && (tabName !== 'myassignment');

    const useStyles = makeStyles((theme) => AssignmentTableStyle(theme));
    const classes = useStyles();
    const history = useHistory();
    const [noteDialog, setNoteDialog] = React.useState(false);
    const [notes, setNotes] = React.useState("");
    const [noteStatus, setNoteStatus] = React.useState("view");
    const [assignmentId, setAssignmentId] = React.useState("");
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [deleteDialog, setDeleteDialog] = React.useState(false);
    const [passFailHistory, setPassFailHistory] = React.useState([]);
    const [trackRecordDialog , setTrackRecordDialog] = React.useState(false);

    useEffect(() => {
        //console.log();
    }, [assignment_loading]);

    /**
     * Handles the click event for a given row, performing specific actions.
     *
     * This function performs the following:
     * - Saves the sim_id of the row to the localStorage.
     * - Redirects the user to the "/sims" route with specific state data.
     * - Persists the current sim product type to maintain context.
     *
     * @param {Object} row - The data object representing the clicked row.
     * @param {string} row.sim_id - The unique identifier for the simulation.
     * @param {string} row.assigned_to_id - The user ID to whom the simulation is assigned.
     * @param {string} row.tool_type - The type of tool associated with the simulation.
     */
    const handleClick = (row) => {
        localStorage.setItem("sim_id", row.sim_id);
        history.push({
            pathname: "/sims",
            state: {...row, user_id: row.assigned_to_id},
        });
        saveCurrentSimProductType(row.tool_type);
    };

    /**
     * Closes various dialog components and resets related state variables.
     *
     * This function performs the following actions:
     * - Sets the visibility of the note dialog to false.
     * - Clears the notes input field by setting it to an empty string.
     * - Resets the assignment ID to an empty string.
     * - Resets the current index to an empty string.
     * - Closes the delete confirmation dialog by setting its visibility to false.
     */
    const handleClose = () => {
        setNoteDialog(false);
        setNotes("");
        setAssignmentId("");
        setCurrentIndex("");
        setDeleteDialog(false);
    };

    const addNotes = () => {
        /**
         * Represents the payload object containing assignment-related information.
         * @typedef {Object} payload
         * @property {string} assignment_notes - Notes or remarks associated with the assignment.
         */
        const payload = {
            assignment_notes: notes,
        };
        saveAssignmentNotes(assignmentId, payload, false);
        setNoteDialog(false);
        setNotes("");
        setAssignmentId("");
        assignments[currentIndex].notes = notes;
    };

    /**
     * Updates the notes for a specific assignment.
     *
     * This function constructs a payload containing the updated assignment notes,
     * then saves the notes to the respective assignment using the provided assignment ID.
     * It also handles resetting the notes input dialog, clears the temporary data,
     * and updates the current state of the assignments list with the new notes.
     *
     * Dependencies:
     * - `saveAssignmentNotes`: A function responsible for persisting the updated assignment notes.
     * - `setNoteDialog`: A function to toggle the state of the notes dialog visibility.
     * - `setNotes`: A function to reset the state of the notes input.
     * - `setAssignmentId`: A function to clear the state of the assignment ID.
     * - `assignments`: An array containing all assignment data.
     *
     * Side Effects:
     * - Updates the `notes` field for the assignment at the current index.
     *
     * Preconditions:
     * - Valid `assignmentId` and `notes` must be set before invoking this function.
     */
    const editNotes = () => {
        const payload = {
            assignment_notes: notes,
        };
        saveAssignmentNotes(assignmentId, payload, true);
        setNoteDialog(false);
        setNotes("");
        setAssignmentId("");
        assignments[currentIndex].notes = notes;
    };

    /**
     * Triggers the process to remove an assignment by opening a delete confirmation dialog.
     *
     * This function sets the state variable `deleteDialog` to `true`, which is used to display
     * a confirmation dialog for deleting an assignment. It is typically invoked when the user
     * initiates an action to remove an assignment, such as clicking a delete button.
     *
     * @function
     * @returns {void} Does not return a value.
     */
    const removeAssignment = () => {
        setDeleteDialog(true);
    };

    /**
     * Handles the deletion of an assignment and updates the related state.
     *
     * This function removes the assignment at the specified index from the assignments list,
     * triggers the deletion of the assignment using its ID, and resets relevant state variables.
     * It also refreshes the assignment count after the deletion operation.
     *
     * Side effects:
     * - Modifies the `assignments` array by removing the assignment at `currentIndex`.
     * - Calls the `deleteAssignment` function to delete the assignment associated with
     * `assignmentId`.
     * - Updates the state variables `deleteDialog`, `assignmentId`, and `currentIndex`.
     * - Triggers `refreshCount` to update the assignment count.
     */
    const deleteConfirm = () => {
        assignments.splice(currentIndex, 1);
        deleteAssignment(assignmentId);
        setDeleteDialog(false);
        setAssignmentId("");
        setCurrentIndex("");
        refreshCount();
    };

    /**
     * Handles the display and retrieval of the assignment's track record information.
     *
     * This function performs the following tasks:
     * - Opens a dialog to display the track record details.
     * - Fetches the assignment history based on the provided assignment ID.
     * - Processes the retrieved data to obtain the pass/fail status history in reverse order.
     *
     * @param {Object} row - An object representing the selected row in the data table.
     * @param {number} row.assignment_id - The unique identifier of the assignment to retrieve the
     *     history for.
     */
    const trackRecord = (row) => {
        setTrackRecordDialog(true);
        getAssignmentHistory(row.assignment_id)
            .then((res) => {
                let history = res.data[0] ? res.data[0].reviewed_status.map((entry) => {
                    return {
                        review_date: entry.review_date,
                        pass_or_fail: entry.pass_or_fail,
                        comments: entry.comments ?? res.data[0].review_comments,
                    }
                }) : [];
                setPassFailHistory(history.reverse());
            });
    };

    /**
     * Closes the track record dialog and resets the pass/fail history.
     *
     * This function performs the following actions:
     * 1. Sets the visibility of the track record dialog to false.
     * 2. Clears the pass/fail history data by resetting it to an empty array.
     *
     * It is typically used to handle the "close" action for the track record dialog.
     */
    const handleTrackClose = () => {
        setTrackRecordDialog(false);
        setPassFailHistory([]);
    }

    /**
     * Escapes special characters in a string to their corresponding HTML entities.
     * Converts characters such as &, <, >, ", and ' to their HTML-safe representations.
     *
     * @param {string} str - The input string to be escaped.
     * @returns {string} The escaped string with special characters replaced.
     */
    const escapeString = (str) => {
        return str.replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&apos;");
    }

    /**
     * Generates the content to display in a table cell for a learner's row, based on their status
     * and review comments.
     *
     * @param {Object} row - The data object representing a row in the learner's table.
     * @param {string} row.status - The current status of the learner (e.g., "PENDING REVIEW",
     *     "COMPLETED", "NO PASS").
     * @param {string} row.assigned_to_id - The ID of the user assigned to the learner.
     * @param {Array} row.review_comments - An array containing review comments for the learner.
     * @returns {JSX.Element} - A rendered table cell element with conditional styling and content
     *     or tooltips based on the status and comments.
     */
    const showLearnersMessage = (row) => {
        let className = `${userId !== row.assigned_to_id && row.status !== "PENDING REVIEW"
                ? (row.review_comments.length ? classes.disabledWithTooltip : classes.disabled)
                : classes.adminNote
            } ${row.status === "NO PASS" ? classes.failedTableRow : ""}`,
            tooltipString = row.review_comments.length > 90 ?
                escapeString(row.review_comments) :
                '',
            cellString = row.review_comments.length > 90 ?
                escapeString(`${row.review_comments.slice(0, 90)}  ...`) :
                escapeString(row.review_comments);

        if (!row.review_comments.length) {
            return (
                <TableCell className={className}>
                    ---------
                </TableCell>
            );
        }

        if (row.status === "COMPLETED") {
            return (
                <Tooltip title={tooltipString}
                         interactive={true}
                >
                    <TableCell className={className}>
                        ---------
                    </TableCell>
                </Tooltip>
            );
        } else {
            return (
                <Tooltip title={tooltipString}
                     interactive={true}
                >
                    <TableCell className={className}>
                        <span dangerouslySetInnerHTML={{__html: cellString}}></span>
                    </TableCell>
                </Tooltip>
            );
        }
    };

    /**
     * Renders an appropriate action button or set of buttons based on the value of `value`
     * and the properties of the passed `row` object. This function customizes the appearance
     * and behavior of the action column in a table depending on the current workflow state
     * such as "myAssignment", "completed", or "readyForReview".
     *
     * @param {Object} row - The current row data object containing details of a table entry.
     *                       Properties such as `id`, `status`, and others are used in decision
     *     making.
     * @param {Number} index - The zero-based index of the current row within the table.
     * @returns {JSX.Element} A `TableCell` component containing the relevant action buttons or
     *                        elements customized for the row and its corresponding state.
     */
    const showAction = (row, index) => {
        if (value === "myAssignment") {
            return (
                <TableCell key={`actions_${row.id}`}>
                    <div className={classes.assignmentActionCell}>
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.button}
                            onClick={() => handleClick(row)}
                            endIcon={<ArrowRightAltIcon fontSize="small" />}
                        >
                            Go
                        </Button>
                    </div>
                </TableCell>
            );
        } else if (value === "completed") {
            return (
                <TableCell key={`actions_${row.id}`}>
                    <div className={classes.completedActionCell}>
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.viewRetryButton}
                            onClick={() => handleClick(row)}
                        >
                            VIEW OR RETRY
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.viewRetryButton}
                            onClick={() => trackRecord(row)}
                        >
                            TRACK RECORD
                        </Button>
                    </div>
                </TableCell>
            );
        } else if (value === "readyForReview") {
            return (
                <TableCell key={`actions_${row.id}`}>
                    <div className={classes.built}>
                        {row.status === "PENDING REVIEW" && (
                            <StyledLink
                                to={{
                                    pathname: "/sims/takesim",
                                    search: `?levelId=${row.sim_level_id}&assignment_id=${row.assignment_id}&sim_id=${row.sim_id}&user_id=${row.assigned_to_id}&order=${row.sim_level_order}`,
                                    state: {
                                        viewResult: true,
                                        // assignment: assignmentData,handleShowReviewerButtons
                                    },
                                }}
                            >
                                <Tooltip title={"Review"} placement="top">
                                    <VisibilityIcon
                                        onClick={() => handleShowReviewerButtons({ payload: true })}
                                        className={classes.viewIcon}
                                        fontSize="small"
                                    />
                                </Tooltip>
                            </StyledLink>
                        )}
                    </div>
                </TableCell>
            );
        } else {
            return (
                <TableCell key={`actions_${row.id}`}>
                    <div className={classes.built}>
                        {!permissionCheck(
                            DashboardPermissions.notes_to_user.update.task,
                            DashboardPermissions.notes_to_user.update.type
                        ) &&
                        !permissionCheck(
                            DashboardPermissions.notes_to_user.create.task,
                            DashboardPermissions.notes_to_user.create.type
                        ) &&
                        permissionCheck(
                            DashboardPermissions.notes_to_user.view.task,
                            DashboardPermissions.notes_to_user.view.type
                        ) ? (
                            <ThemeProvider theme={innerTheme}>
                                <Tooltip title="View Instructions">
                                    <EventNoteIcon
                                        className={classes.viewNote}
                                        onClick={() => {
                                            setNoteDialog(true);
                                            setAssignmentId(row.assignment_id);
                                            setNotes(row.notes);
                                            setNoteStatus("view");
                                            setCurrentIndex(index);
                                        }}
                                    />
                                </Tooltip>
                            </ThemeProvider>
                        ) : permissionCheck(
                            DashboardPermissions.notes_to_user.update.task,
                            DashboardPermissions.notes_to_user.update.type
                        ) ||
                        permissionCheck(
                            DashboardPermissions.notes_to_user.create.task,
                            DashboardPermissions.notes_to_user.create.type
                        ) ? (
                            <ThemeProvider theme={innerTheme}>
                                <Tooltip title="Add Instructions">
                                    {/*<Icon
                                        onClick={() => {
                                            setNoteDialog(true);
                                            setAssignmentId(row.assignment_id);
                                            setNotes(row.notes);
                                            setNoteStatus(row.notes === " " ? "add" : "edit");
                                            setCurrentIndex(index);
                                        }}
                                    >*/}
                                        <EditIcon className={classes.edit}
                                                  height={17}
                                                  onClick={() => {
                                                      setNoteDialog(true);
                                                      setAssignmentId(row.assignment_id);
                                                      setNotes(row.notes);
                                                      setNoteStatus(row.notes === " " ? "add" : "edit");
                                                      setCurrentIndex(index);
                                                  }}
                                        />
                                        {/*<img
                                            className={classes.edit}
                                            height={17}
                                            src={Edit}
                                            alt="edit"
                                        />*/}
                                    {/*</Icon>*/}
                                </Tooltip>
                            </ThemeProvider>
                        ) : null}
                        {row.status === "NOT STARTED" ? (
                            <Tooltip
                                title={
                                    !permissionCheck(
                                        SimsPermissions.assignment.delete.task,
                                        SimsPermissions.assignment.delete.type
                                    )
                                        ? "You don't have permission to delete"
                                        : "Delete Assignment"
                                }
                            >
                                <DeleteIcon onClick={
                                    !permissionCheck(
                                        SimsPermissions.assignment.delete.task,
                                        SimsPermissions.assignment.delete.type
                                    )
                                        ? () => { }
                                        : () => {
                                            removeAssignment();
                                            setCurrentIndex(index);
                                            setAssignmentId(row.assignment_id);
                                        }
                                    }
                                    disabled={true}
                                    className={
                                        !permissionCheck(
                                            SimsPermissions.assignment.delete.task,
                                            SimsPermissions.assignment.delete.type
                                        )
                                            ? classes.disabledDelete
                                            : classes.delete
                                    }
                                >

                                </DeleteIcon>
                                {/*
                                <Icon
                                    onClick={
                                        !permissionCheck(
                                            SimsPermissions.assignment.delete.task,
                                            SimsPermissions.assignment.delete.type
                                        )
                                            ? () => { }
                                            : () => {
                                                removeAssignment();
                                                setCurrentIndex(index);
                                                setAssignmentId(row.assignment_id);
                                            }
                                    }
                                    disabled={true}
                                >
                                    <img
                                        className={
                                            !permissionCheck(
                                                SimsPermissions.assignment.delete.task,
                                                SimsPermissions.assignment.delete.type
                                            )
                                                ? classes.disabledDelete
                                                : classes.delete
                                        }
                                        height={17}
                                        src={Delete}
                                        alt="delete"
                                    />
                                </Icon>*/}
                            </Tooltip>
                        ) : (
                            <></>
                        )}
                        {row.status === "PENDING REVIEW" && (
                            <StyledLink
                                to={{
                                    pathname: "/sims/takesim",
                                    search: `?levelId=${row.sim_level_id}&assignment_id=${row.assignment_id}&sim_id=${row.sim_id}&user_id=${row.assigned_to_id}&reviewer=true&order=${row.sim_level_order}`,
                                    state: {
                                        viewResult: true,
                                        // assignment: assignmentData,handleShowReviewerButtons
                                    },
                                }}
                            >
                                <Tooltip title={"Review"} placement="top">
                                    <VisibilityIcon
                                        onClick={() => handleShowReviewerButtons({ payload: true })}
                                        style={{ color: "#882847", marginLeft: ".6rem" }}
                                        fontSize="small"
                                    />
                                </Tooltip>
                            </StyledLink>
                        )}
                    </div>
                </TableCell>
            );
        }
    };

    const showPassFailHistory = () => {
        if (passFailHistory.length) {
            return (
                <>
                    {passFailHistory.map((pf, index) => (
                        <TableRow key={index}>
                            <TableCell>{moment(pf.review_date).format('MM-DD-YYYY')}</TableCell>
                            <TableCell>{pf.pass_or_fail}</TableCell>
                            <TableCell>
                                {pf.comments ?? passFailHistory.review_comments}
                            </TableCell>
                        </TableRow>
                    ))}
                </>
            );
        }

        return (
            <TableRow>
                <TableCell colSpan={3} className={classes.noRecord} style={{ textAlign: "center" }}>
                    No previous results..
                </TableCell>
            </TableRow>
        );
    }

    return (
        <div>
            <Dialog
                onClose={handleClose}
                open={noteDialog}
                classes={{ paper: classes.dialog }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Instruction</DialogTitle>
                <DialogContent>
                    <div style={{ padding: "0.5rem", width: "23rem" }}>
                        <TextField
                            InputProps={{
                                disableUnderline: true,
                                style: { fontSize: 14, paddingLeft: 8 },
                            }}
                            fullWidth
                            multiline
                            minRows={5}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className={classes.textArea}
                            placeholder="Type your note here"
                            disabled={
                                !permissionCheck(
                                    DashboardPermissions.notes_to_user.update.task,
                                    DashboardPermissions.notes_to_user.update.type
                                ) &&
                                !permissionCheck(
                                    DashboardPermissions.notes_to_user.create.task,
                                    DashboardPermissions.notes_to_user.create.type
                                ) &&
                                permissionCheck(
                                    DashboardPermissions.notes_to_user.view.task,
                                    DashboardPermissions.notes_to_user.view.type
                                )
                            }
                        />
                        <Button
                            size="small"
                            style={{
                                fontWeight: 600,
                                padding: "3px 11px",
                                backgroundColor: "#F8DEE7",
                                float: "right",
                                color: "#A55B73",
                                margin: "8px 0px",
                            }}
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        {noteStatus === "edit" ? (
                            <Tooltip
                                title={
                                    !permissionCheck(
                                        DashboardPermissions.notes_to_user.update.task,
                                        DashboardPermissions.notes_to_user.update.type
                                    )
                                        ? "You don't have permission to update"
                                        : ""
                                }
                                placement="top"
                            >
                                <span className={classes.editSpan}>
                                    <Button
                                        size="small"
                                        className={classes.dialogButton}
                                        disabled={
                                            !permissionCheck(
                                                DashboardPermissions.notes_to_user.update.task,
                                                DashboardPermissions.notes_to_user.update.type
                                            )
                                        }
                                        onClick={editNotes}
                                    >
                                        Edit
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : noteStatus === "add" ? (
                            <Tooltip
                                title={
                                    !permissionCheck(
                                        DashboardPermissions.notes_to_user.create.task,
                                        DashboardPermissions.notes_to_user.create.type
                                    )
                                        ? "You don't have permission to create"
                                        : ""
                                }
                                placement="top"
                            >
                                <span className={classes.editSpan}>
                                    <Button
                                      size="small"
                                      className={classes.dialogButton}
                                      disabled={
                                          !permissionCheck(
                                              DashboardPermissions.notes_to_user.create.task,
                                              DashboardPermissions.notes_to_user.create.type
                                          )
                                      }
                                      onClick={addNotes}
                                    >
                                        Add
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : null}
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog
                open={deleteDialog}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Delete Assignment</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {`Do you want to delete the assignment "${assignments[currentIndex]?.assignment}"`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} className={classes.cancelButton}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => deleteConfirm()}
                        className={classes.dialogDeleteBtn}
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={trackRecordDialog}
                onClose={handleTrackClose}
                className={classes.triesDialog}
            >
                <DialogTitle id="alert-dialog-title">Reviewed Result</DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <TableContainer>
                        <Table className={classes.tableBorder}>
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    <TableCell className={classes.tableCell}>REVIEW DATE</TableCell>
                                    <TableCell className={classes.tableCell}>RESULT</TableCell>
                                    <TableCell className={classes.tableCell}>COMMENTS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {showPassFailHistory()}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleTrackClose} className={classes.cancelButton}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {assignment_loading && assignments.length === 0 ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "70vh",
                    }}
                >
                    <CircularProgress color="primary" />
                </div>
            ) : (
                <Paper>
                    <TableContainer className={classes.tableContainer}>
                        <Table aria-label="assignment table" className={classes.table}>
                            <TableHead className={classes.thead}>
                                <TableRow>
                                    {!showAssignedTo ? null : (
                                        <TableCell className={classes.heading} sortDirection={orderBy === 'assigned_to' ? order : false} >
                                            <TableSortLabel
                                                direction={orderBy === 'assigned_to' ? order : 'asc'}
                                                active
                                                // ={orderBy === 'assigned_to'}
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={() => sortProperties('assigned_to')}
                                            >
                                                ASSIGNED TO
                                            </TableSortLabel>
                                        </TableCell>
                                    )}

                                    {value === "allAssignment" ? (
                                        <TableCell className={classes.heading} sortDirection={orderBy === 'group' ? order : false}>
                                            <TableSortLabel
                                                direction={orderBy === 'group' ? order : 'asc'}
                                                active
                                                // ={orderBy === 'group'}
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={() => sortProperties('groups')}
                                            >
                                                GROUP
                                            </TableSortLabel>
                                        </TableCell>
                                    ) : null}

                                    <TableCell className={classes.heading} sortDirection={orderBy === 'tool_type' ? order : false}>
                                        <TableSortLabel
                                            direction={orderBy === 'tool_type' ? order : 'asc'}
                                            active
                                            // ={orderBy === 'tool_type'}
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sortProperties('tool_type')}
                                        >
                                            TOOL TYPE
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell className={classes.heading} sortDirection={orderBy === 'assignment' ? order : false}>
                                        <TableSortLabel
                                            direction={orderBy === 'assignment' ? order : 'asc'}
                                            active
                                            // ={orderBy === 'assignment'}
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sortProperties('assignment')}
                                        >
                                            ASSIGNMENT
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell className={classes.heading} sortDirection={orderBy === 'assigned_on' ? order : false}>
                                        <TableSortLabel
                                            direction={orderBy === 'assigned_on' ? order : 'asc'}
                                            active
                                            // ={orderBy === 'assigned_on'}
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sortProperties('assigned_on')}
                                        >
                                            ASSIGNED ON
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell className={classes.heading} sortDirection={orderBy === 'assigned_by' ? order : false}>
                                        <TableSortLabel
                                            direction={orderBy === 'assigned_by' ? order : 'asc'}
                                            active
                                            // ={orderBy === 'assigned_by'}
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sortProperties('assigned_by')}
                                        >
                                            ASSIGNED BY
                                        </TableSortLabel>
                                    </TableCell>

                                    {value === "myAssignment" || value === "completed" || value === "allAssignment" ? (
                                        <TableCell className={classes.heading} sortDirection={orderBy === 'notes' ? order : false}>
                                            <TableSortLabel
                                                direction={orderBy === 'notes' ? order : 'asc'}
                                                active
                                                // ={orderBy === 'notes'}
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={() => sortProperties('notes')}
                                            >
                                                INSTRUCTION
                                            </TableSortLabel>
                                        </TableCell>
                                    ) : null}

                                    {value === "completed" ? (
                                        <TableCell className={classes.heading} sortDirection={orderBy === 'review_comments' ? order : false}>
                                            <TableSortLabel
                                                direction={orderBy === 'review_comments' ? order : 'asc'}
                                                active
                                                // ={orderBy === 'review_comments'}
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={() => sortProperties('review_comments')}
                                            >
                                                MESSAGE TO LEARNER
                                            </TableSortLabel>
                                        </TableCell>
                                    ) : null}

                                    {value === "completed" ? (
                                        <TableCell className={classes.heading} sortDirection={orderBy === 'start_date' ? order : false}>
                                            <TableSortLabel
                                                direction={orderBy === 'start_date' ? order : 'asc'}
                                                active
                                                // ={orderBy === 'start_date'}
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={() => sortProperties('start_date')}
                                            >
                                                START DATE
                                            </TableSortLabel>
                                        </TableCell>
                                    ) : null}

                                    {value === "completed" ? (
                                        <TableCell className={classes.heading} sortDirection={orderBy === 'send_for_review_date' ? order : false}>
                                            <TableSortLabel
                                                direction={orderBy === 'send_for_review_date' ? order : 'asc'}
                                                active
                                                // ={orderBy === 'send_for_review_date'}
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={() => sortProperties('send_for_review_date')}
                                            >
                                                SENT FOR REVIEW DATE
                                            </TableSortLabel>
                                        </TableCell>
                                    ) : null}

                                    {value === "completed" ? (
                                        <TableCell className={classes.heading} sortDirection={orderBy === 'date_of_review' ? order : false}>
                                            <TableSortLabel
                                                direction={orderBy === 'date_of_review' ? order : 'asc'}
                                                active
                                                // ={orderBy === 'date_of_review'}
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={() => sortProperties('date_of_review')}
                                            >
                                                DATE OF REVIEW
                                            </TableSortLabel>
                                        </TableCell>
                                    ) : null}

                                    {value === "readyForReview" ? null : (
                                        <TableCell className={classes.heading} sortDirection={orderBy === 'status' ? order : false}>
                                            <TableSortLabel
                                                direction={orderBy === 'status' ? order : 'asc'}
                                                active
                                                // ={orderBy === 'status'}
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={() => sortProperties('status')}
                                            >
                                                STATUS
                                            </TableSortLabel>
                                        </TableCell>
                                    )}

                                    {value === "readyForReview" ? (
                                        <TableCell className={classes.heading} sortDirection={orderBy === 'send_for_review_date' ? order : false}>
                                            <TableSortLabel
                                                direction={orderBy === 'send_for_review_date' ? order : 'asc'}
                                                active
                                                // ={orderBy === 'send_for_review_date'}
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={() => sortProperties('send_for_review_date')}
                                            >
                                                COMPLETED ON
                                            </TableSortLabel>
                                        </TableCell>
                                    ) : null}

                                    <TableCell className={classes.heading}>ACTIONS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody className={classes.tbody}>
                                {assignments.map((row, index) => (
                                    <StyledTableRow
                                        key={index}
                                        className={
                                            row.status === "PENDING REVIEW" &&
                                            value === "allAssignment" &&
                                            !inReadyReviewTab
                                                ? classes.rfrTableRow
                                                : value === "myAssignment" ||
                                                value === "completed" ||
                                                value === "readyForReview"
                                                    ? classes.cell
                                                    : classes.tableRow
                                        }
                                    >
                                        {!showAssignedTo ? null : (
                                            /* Assigned To */
                                            <TableCell
                                                className={classes.clickable}
                                                onClick={() => handleClick(row)}
                                            >
                                                <div className={classes.built}>
                                                    <Avatar
                                                        src={
                                                            row.assigned_profile_url
                                                                ? row.assigned_profile_url
                                                                : ""
                                                        }
                                                        alt="profile"
                                                        style={{ marginRight: "10px" }}
                                                        className={classes.small}
                                                    />
                                                    <div className={classes.name}>{row.assigned_to}</div>
                                                </div>
                                            </TableCell>
                                        )}
                                        {value === "allAssignment" ? (
                                            <TableCell
                                                // className={
                                                //   userId !== row.assigned_to_id &&
                                                //     row.status !== "PENDING REVIEW"
                                                //     ? classes.disabled
                                                //     : ""
                                                // }
                                                onClick={() => handleClick(row)}
                                            >
                                                { (row.groups.toString()).replace(/,/g, ', ')}
                                            </TableCell>
                                        ) : null}
                                        {value === "allAssignment" ? (
                                            <TableCell
                                                onClick={() => handleClick(row)}
                                                // className={
                                                //   userId !== row.assigned_to_id &&
                                                //     row.status !== "PENDING REVIEW"
                                                //     ? classes.disabled
                                                //     : ""
                                                // }
                                            >
                                                {row.tool_type}
                                            </TableCell>
                                        ) : (
                                            <TableCell
                                                className={`${userId !== row.assigned_to_id &&
                                                row.status !== "PENDING REVIEW"
                                                    ? classes.disabled
                                                    : ""
                                                } ${row.status === "NO PASS" ? classes.failedTableRow : ""
                                                }`}
                                            >
                                                {row.tool_type}
                                            </TableCell>
                                        )}
                                        {value === "allAssignment" ? (
                                            <TableCell
                                                onClick={() => handleClick(row)}
                                                // className={
                                                //   userId !== row.assigned_to_id &&
                                                //     row.status !== "PENDING REVIEW"
                                                //     ? classes.disabled
                                                //     : classes.overflow
                                                // }
                                            >
                                                <Tooltip title={row?.assignment?.length === 0 ? 'None' : row.assignment}>
                                                    <>
                                                    {(row?.screen_recording?.length ) ?
                                                        (
                                                            <Tooltip title={'A level in this SIM allows screen share recordings'}
                                                                     placement="top"
                                                            >
                                                                <div style={{
                                                                    marginRight: '1em',
                                                                    verticalAlign: 'middle',
                                                                    display: 'inline-block',
                                                                }}>
                                                                    <ScreenShareIcon fontSize="small"/>
                                                                </div>
                                                            </Tooltip>
                                                        ) : (<></>)
                                                    }
                                                    {row?.screen_recording_urls?.length ? (
                                                        <Tooltip
                                                            title={`User has ${row.screen_recording_urls.length} screen recordings in one or more levels of this SIM`}
                                                            placement="top"
                                                        >
                                                            <div style={{
                                                                marginRight: '1em',
                                                                verticalAlign: 'middle',
                                                                display: 'inline-block',
                                                            }}>
                                                                <VideocamIcon fontSize="small" />
                                                            </div>
                                                        </Tooltip>
                                                    ) : (<></>)}
                                                    {row?.assignment?.length > 50
                                                        ? `${row.assignment.slice(0, 50)}  ...`
                                                        : row.assignment}
                                                    </>
                                                </Tooltip>
                                            </TableCell>
                                        ) : (
                                            <TableCell
                                                className={`${userId !== row.assigned_to_id &&
                                                row.status !== "PENDING REVIEW"
                                                    ? ''
                                                    : classes.overflow
                                                } ${row.status === "NO PASS" ? classes.failedTableRow : ""
                                                }`}
                                            >
                                                <Tooltip title={row?.assignment?.length === 0 ? 'None' : row.assignment}>
                                                    <>
                                                    {(row?.screen_recording?.length ) ?
                                                        (
                                                            <Tooltip title={'A level in this SIM allows screen share recordings'}
                                                                      placement="top"
                                                            >
                                                                <div style={{
                                                                    marginRight: '1em',
                                                                    verticalAlign: 'middle',
                                                                    display: 'inline-block',
                                                                }}>
                                                                    <ScreenShareIcon fontSize="small"/>
                                                                </div>
                                                            </Tooltip>
                                                        ) : (<></>)
                                                    }
                                                    {row?.screen_recording_urls?.length ? (
                                                        <Tooltip
                                                            title={`User has ${row.screen_recording_urls.length} screen recordings in one or more levels of this SIM`}
                                                            placement="top"
                                                        >
                                                            <div style={{
                                                                marginRight: '1em',
                                                                verticalAlign: 'middle',
                                                                display: 'inline-block',
                                                            }}>
                                                                <VideocamIcon fontSize="small" />
                                                            </div>
                                                        </Tooltip>
                                                    ) : (<></>)}

                                                    {row?.assignment?.length > 50
                                                        ? `${row.assignment.slice(0, 50)}  ...`
                                                        : row.assignment}
                                                    </>
                                                </Tooltip>
                                            </TableCell>
                                        )}
                                        {/* <TableCell>{row.limit}</TableCell> */}
                                        {value === "allAssignment" ? (
                                            <TableCell
                                                onClick={() => handleClick(row)}
                                                // className={
                                                //   userId !== row.assigned_to_id &&
                                                //     row.status !== "PENDING REVIEW"
                                                //     ? classes.disabled
                                                //     : ""
                                                // }
                                            >
                                                {moment(new Date(row.assigned_on)).format(
                                                    "MMM DD YYYY"
                                                )}
                                            </TableCell>
                                        ) : (
                                            <TableCell
                                                className={`${row.status === "NO PASS" ? classes.failedTableRow : ""
                                                }`}
                                            >
                                                {moment(new Date(row.assigned_on)).format(
                                                    "MMM DD YYYY"
                                                )}
                                            </TableCell>
                                        )}
                                        {value === "allAssignment" ? (
                                            <TableCell
                                                onClick={() => handleClick(row)}
                                                // className={
                                                //   userId !== row.assigned_to_id &&
                                                //     row.status !== "PENDING REVIEW"
                                                //     ? classes.disabled
                                                //     : ""
                                                // }
                                            >
                                                {row.assigned_by}
                                            </TableCell>
                                        ) : (
                                            <TableCell
                                                className={`${row.status === "NO PASS" ? classes.failedTableRow : ""
                                                }`}
                                            >
                                                {row.assigned_by}
                                            </TableCell>
                                        )}

                                        {/* Instruction */}
                                        {value === "allAssignment" || value === "myAssignment" || value === "completed" ? (
                                            <Tooltip
                                                title={row.notes?.length > 90 ? escapeString(row.notes) : ''}
                                                interactive={true}
                                            >
                                                <TableCell
                                                    className={`${userId !== row.assigned_to_id &&
                                                    row.status !== "PENDING REVIEW"
                                                        ? ''
                                                        : classes.adminNote
                                                    } ${row.status === "NO PASS" ? classes.failedTableRow : ""
                                                    }`}
                                                >
                                                    {row?.notes?.length > 90
                                                        ? escapeString(`${row.notes.slice(0, 90)}  ...`)
                                                        : escapeString(row.notes)}
                                                </TableCell>
                                            </Tooltip>
                                        ) : null}

                                        {/* Message to Learner */}
                                        {value === "completed" ? showLearnersMessage(row) : null}

                                        {/* Start Date */}
                                        {value === "completed" ? (
                                            <TableCell
                                                className={`${userId !== row.assigned_to_id &&
                                                row.status !== "PENDING REVIEW"
                                                    ? classes.disabled
                                                    : ""
                                                } ${row.status === "NO PASS" ? classes.failedTableRow : ""
                                                }`}
                                            >
                                                {moment(new Date(row.start_date)).format("MMM DD YYYY")}
                                            </TableCell>
                                        ) : null}

                                        {/* Sent for review date */}
                                        {value === "completed" ? (
                                            <TableCell
                                                className={`${userId !== row.assigned_to_id &&
                                                row.status !== "PENDING REVIEW"
                                                    ? classes.disabled
                                                    : ""
                                                } ${row.status === "NO PASS" ? classes.failedTableRow : ""
                                                }`}
                                            >
                                                {row.send_for_review_date
                                                    ? moment(new Date(row.send_for_review_date)).format(
                                                        "MMM DD YYYY"
                                                    )
                                                    : "---------"}
                                            </TableCell>
                                        ) : null}

                                        {/* Date of review */}
                                        {value === "completed" ? (
                                            <TableCell
                                                className={`${userId !== row.assigned_to_id &&
                                                row.status !== "PENDING REVIEW"
                                                    ? classes.disabled
                                                    : ""
                                                } ${row.status === "NO PASS" ? classes.failedTableRow : ""
                                                }`}
                                            >
                                                {row.date_of_review
                                                    ? moment(new Date(row.date_of_review)).format(
                                                        "MMM DD YYYY"
                                                    )
                                                    : "---------"}
                                            </TableCell>
                                        ) : null}

                                        {/*Status*/}
                                        {value === "readyForReview" ? null : value ===
                                        "allAssignment" ? (
                                            <TableCell
                                                padding="none"
                                                onClick={() => handleClick(row)}
                                                className={`${row.status === "NO PASS" ? classes.failedTableRow : ""
                                                }`}
                                            >
                                                {row.status}
                                            </TableCell>
                                        ) : (
                                            <TableCell
                                                padding="none"
                                                className={`${userId !== row.assigned_to_id &&
                                                row.status !== "PENDING REVIEW"
                                                    ? classes.disabled
                                                    : ""
                                                } ${row.status === "NO PASS" ? classes.failedTableRow : ""
                                                }`}
                                            >
                                                {row.status}
                                            </TableCell>
                                        )}

                                        {/*Send for review date*/}
                                        {value === "readyForReview" ? (
                                            <TableCell
                                                className={
                                                    userId !== row.assigned_to_id &&
                                                    row.status !== "PENDING REVIEW"
                                                        ? classes.disabled
                                                        : ""
                                                }
                                            >
                                                {moment(new Date(row.send_for_review_date)).format(
                                                    "MMM DD YYYY"
                                                )}
                                            </TableCell>
                                        ) : null}

                                        {/*Actions*/}
                                        {showAction(row, index)}
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <div>
                        <div className={classes.paginationLabel}>
                            Showing
                            {' '}
                            {(page - 1) * 10 + 1}
                            {' '}
                            to
                            {' '}
                            {(page - 1) * 10
                                + (assignments && assignments.length ? assignments.length : 0)}
                            {' '}
                            of
                            {' '}
                            {total ? total : 0}
                            {' '}
                            entries
                        </div>
                        <Pagination
                            className={classes.pagination}
                            color="primary"
                            count={
                                total
                                    ? Math.ceil(total / 10)
                                    : 0
                            }
                            shape="rounded"
                            page={page}
                            onChange={handlePageChange}
                        />
                    </div>
                </Paper>
            )}
            {!assignment_loading && assignments.length === 0 ? (
                <div className={classes.noData}>
                    <img src={noAssignments} height={55} width={54} alt="noAssignments" />
                    <h3 className={classes.noAssign}>
                        {value === "allAssignment"
                            ? "There are no Assignments added yet!"
                            : "There are no Assignments."}
                    </h3>
                    {value === "allAssignment" &&
                    permissionCheck(
                        SimsPermissions.assignment.create.task,
                        SimsPermissions.assignment.create.type
                    ) ? (
                        <>
                            <p className={classes.noAssignPara}>
                                Let&apos;s get started with first Assignment
                            </p>
                            <Button
                                variant="outlined"
                                color="primary"
                                className={classes.addButton}
                                onClick={addAssignment}
                            >
                                <span className={classes.plus}>+</span> ADD ASSIGNMENT
                            </Button>
                        </>
                    ) : (
                        ""
                    )}
                </div>
            ) : null}
        </div>
    );
};

AssignmentTable.propTypes = {
    assignments: PropTypes.array,
    total: PropTypes.number,
    sortProperties: PropTypes.func,
    order: PropTypes.string,
    orderBy: PropTypes.string,
    value: PropTypes.string,
    handleShowReviewerButtons: PropTypes.func,
    saveAssignmentNotes: PropTypes.func,
    deleteAssignment: PropTypes.func,
    refreshCount: PropTypes.func,
    assignment_loading: PropTypes.bool,
    handlePageChange: PropTypes.func,
    page: PropTypes.number,
    saveCurrentSimProductType: PropTypes.func,
    inReadyReviewTab: PropTypes.bool,
    addAssignment: PropTypes.func,
};

const mapDispatchToProps = {
    handleShowReviewerButtons,
    saveAssignmentNotes,
    deleteAssignment,
    saveCurrentSimProductType
};

export default connect(null, mapDispatchToProps)(AssignmentTable);
